import { createUploadLink } from 'apollo-upload-client';

import {
    createAuthLink,
    createErrorLink,
    createFirebaseTokenLink,
    createLogoutLink,
    CreateRefreshLink,
    createRefreshLink,
    createReplaceTokensLink,
} from '@speeki/apollo-utils';
import {
    SPEEKI_REPORT_SOURCE_HEADER,
    TOKEN_EXPIRED,
    USER_SOURCE,
} from '@speeki/dictionary';
import { createSentryLink } from '@speeki/frontend-sentry';
import { ModalsProvider, notify } from '@speeki/global-ui-components';

import { RefreshSessionTokenDocument } from '@graphql/generated/graphql';

import { APIService } from '@services/ApiService';
import { AuthService } from '@services/AuthService/AuthService';
import { sentry } from '@services/Sentry';

import { ApolloLink, from } from '@apollo/client';
import { client } from '@boot/apolloClient';
import { logoutProcessingVar } from '@boot/reactiveVariables';

const headers = { 'Apollo-Require-Preflight': 'true' };

const directionalLink = ApolloLink.split(
    (operation) => operation.getContext().globalUrl as boolean,
    //@ts-expect-error FIXME
    createUploadLink({
        headers,
        uri: (operation) =>
            `${new APIService().getGlobalGraphAPI}?${operation.operationName}`,
    }),
    createUploadLink({
        headers,
        uri: (operation) =>
            `${new APIService().getGraphAPI}?${operation.operationName}`,
    }),
);
const fetchNewSessionToken: CreateRefreshLink['fetchNewSessionToken'] = (
    refreshToken,
) =>
    client
        .mutate({
            mutation: RefreshSessionTokenDocument,
            variables: {
                token: refreshToken,
            },
        })
        .then((res) => {
            const data = res.data?.refreshSession;

            data?.sessionToken &&
                AuthService.setSessionToken(data.sessionToken);
            data?.refreshToken &&
                AuthService.setRefreshToken(data.refreshToken);
        });

const allowedErrors = [TOKEN_EXPIRED];

const errorLink = createErrorLink({
    allowedErrors,
    onNotify: (message) => notify(message).ERROR(),
});

export const links = from([
    createRefreshLink({
        fetchNewSessionToken,
        getRefreshToken: () => AuthService.getRefreshToken(),
        onRefreshError: () => void AuthService.logout(true),
        onTokenExpiredNoRefresh: () => {
            ModalsProvider.open('SessionExpiredModal');
            void AuthService.logout(true);
        },
    }),
    createLogoutLink({
        onLogout: (error) => {
            if (!logoutProcessingVar()) {
                notify(error.message).ERROR();
                void AuthService.logout(true);
            }
        },
    }),
    createReplaceTokensLink({
        getRefreshToken: () => AuthService.getRefreshToken(),
        setRefreshToken: (token) => AuthService.setRefreshToken(token),
        setSessionToken: (token) => {
            AuthService.setSessionToken(token);
            void sentry.setUser();
        },
    }),
    createAuthLink({
        additionalHeaders: {
            [SPEEKI_REPORT_SOURCE_HEADER]: `${USER_SOURCE.WEBPORTAL}`,
        },
        getSessionToken: () => AuthService.getSessionToken(),
    }),
    errorLink,
    createFirebaseTokenLink(),
    createSentryLink(sentry),
    directionalLink,
]);
